import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AccessibilityWidget.css';

const DEFAULT_SETTINGS = {
  fontSize: 0,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
};

function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('a11y-settings');
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  const applySettings = useCallback((s) => {
    const html = document.documentElement;

    // Font size
    const base = 100 + s.fontSize * 10;
    html.style.fontSize = base === 100 ? '' : `${base}%`;

    // Toggle classes
    html.classList.toggle('a11y-high-contrast', s.highContrast);
    html.classList.toggle('a11y-highlight-links', s.highlightLinks);
    html.classList.toggle('a11y-dyslexia-font', s.dyslexiaFont);
    html.classList.toggle('a11y-reduced-motion', s.reducedMotion);
  }, []);

  useEffect(() => {
    applySettings(settings);
    try {
      localStorage.setItem('a11y-settings', JSON.stringify(settings));
    } catch { /* storage full */ }
  }, [settings, applySettings]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
  const resetAll = () => setSettings(DEFAULT_SETTINGS);

  const changeFontSize = (delta) => {
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.max(-3, Math.min(5, prev.fontSize + delta)),
    }));
  };

  return (
    <div className="a11y-widget">
      <button
        ref={toggleRef}
        className="a11y-widget__toggle"
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Accessibility options"
        aria-expanded={isOpen}
        aria-controls="a11y-panel"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 5H3v2h6.5v4l-2 7h2.2l1.8-6.3L13.3 20h2.2l-2-7V9H20V7Z"/>
        </svg>
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          id="a11y-panel"
          className="a11y-widget__panel"
          role="dialog"
          aria-label="Accessibility settings"
        >
          <h3 className="a11y-widget__title">Accessibility</h3>

          {/* Font Size */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Font Size</span>
            <div className="a11y-widget__font-controls">
              <button
                className="a11y-widget__font-btn"
                onClick={() => changeFontSize(-1)}
                aria-label="Decrease font size"
                disabled={settings.fontSize <= -3}
              >
                A&minus;
              </button>
              <button
                className="a11y-widget__font-btn"
                onClick={() => changeFontSize(1)}
                aria-label="Increase font size"
                disabled={settings.fontSize >= 5}
              >
                A+
              </button>
            </div>
          </div>

          {/* High Contrast */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">High Contrast</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.highContrast}
                onChange={(e) => update('highContrast', e.target.checked)}
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Highlight Links */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Highlight Links</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.highlightLinks}
                onChange={(e) => update('highlightLinks', e.target.checked)}
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Dyslexia Font */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Dyslexia Font</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.dyslexiaFont}
                onChange={(e) => update('dyslexiaFont', e.target.checked)}
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Reduce Motion */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Reduce Motion</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.reducedMotion}
                onChange={(e) => update('reducedMotion', e.target.checked)}
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Reset */}
          <button className="a11y-widget__reset" onClick={resetAll}>
            Reset All
          </button>
        </div>
      )}
    </div>
  );
}

export default AccessibilityWidget;
